/**
 * IndexedDB schema.
 *
 * `opLog` is the client's own source of truth, not a cache: it holds both
 * committed envelopes (with a `seq`) and the outbox (without one), which is
 * precisely the distinction `RenderModel` draws between its two halves. Keeping
 * them in ONE store ordered by `localOrdinal` means catch-up and flush read the
 * same rows in the same order, so there is no way for the two to disagree about
 * what the client has and has not sent.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  AutofocusSession,
  CommittedEnvelope,
  Notebook,
  Page,
  SyncEnvelope,
  Task,
} from '../types/generated';

/** How a row in `opLog` is stored. */
export interface OpRow {
  /** UUIDv7, client-minted. The idempotency key (I5). */
  op_id: string;
  notebook_id: string;
  device_id: string;
  /** Monotonic, per-device, assigned at authoring time. */
  localOrdinal: number;
  /** Present once the server has sequenced this op. */
  seq: number | null;
  /** The authored intent, position-free. */
  envelope: SyncEnvelope;
  /** The server's committed record, once acknowledged. */
  committed: CommittedEnvelope | null;
}

/** First-run identity and the current notebook pointer. */
export interface MetaRow {
  key: string;
  value: unknown;
}

export interface NotebookRow extends Notebook {
  id: string;
}

export interface PageRow extends Page {}

export interface TaskRow extends Task {}

export interface SessionRow extends AutofocusSession {
  notebook_id: string;
}

/** Projection cache — rebuilt from `opLog`, never authoritative (I7). */
export interface ProjectionStore {
  notebooks: { key: string; value: NotebookRow };
  pages: { key: string; value: PageRow; indexes: { byNotebook: string } };
  tasks: {
    key: string;
    value: TaskRow;
    indexes: { byNotebook: string; byPage: string };
  };
  session: { key: string; value: SessionRow };
  meta: { key: string; value: MetaRow };
  opLog: {
    key: string;
    value: OpRow;
    indexes: { byNotebook: string; byOrdinal: number; bySeq: number };
  };
}

/**
 * The synthetic owner (ARCHITECTURE.md §3): the one unclaimed, pre-account
 * owner. Every notebook minted before anyone signs in is authored by this id —
 * the sentinel means "no account yet", not "one person, several notebooks".
 *
 * It lives here rather than beside the engine because it is also the DEFAULT of
 * the browser's acting owner (below), which is what names the local database.
 * `operations.ts` re-exports it, where the engine and the UI have always read it.
 */
export const ANONYMOUS_OWNER_ID = '9f2c7a4e-5d1b-4a8c-b3e6-2f7d9c1a5b4e';

/** Prefix of every local database; the owner is the rest of the name. */
const DB_PREFIX = 'autofocus';
const DB_VERSION = 1;

/**
 * The one database this app used before the store was scoped per owner: one
 * shared `autofocus`, whichever identity was acting.
 *
 * Nothing reads it any more — per-owner scoping means every open goes to
 * `autofocus:<owner>` — but an existing browser's unsynced outbox lives in it,
 * and "offline-authored work silently stops existing" is the one failure this
 * project forbids. [`adoptLegacyStore`] is what carries it across.
 */
const LEGACY_DB_NAME = DB_PREFIX;

/** Every store in {@link ProjectionStore}, so the adoption cannot miss one. */
const STORE_NAMES = ['opLog', 'notebooks', 'pages', 'tasks', 'session', 'meta'] as const;

/** The `meta` key the legacy store is marked with once it has been adopted. */
const LEGACY_ADOPTED_KEY = 'legacyAdopted';

/**
 * Who this browser is acting as, for storage purposes.
 *
 * One module-level value rather than a field on each call site, because an
 * account is a property of the BROWSER SESSION, not of a function: the database
 * a write lands in and the owner the same write is addressed to must never be
 * two different answers. `setDbOwner` is the only writer.
 */
let dbOwner = ANONYMOUS_OWNER_ID;

/** The owner this browser is acting as (design §5). */
export function currentDbOwner(): string {
  return dbOwner;
}

/**
 * The database name for one owner.
 *
 * Per-owner scoping is the design's answer to a shared browser: a person's
 * cached notebooks and log live in their own store, so signing out leaves it
 * intact and the next person to sign in cannot inherit it (design §5).
 */
function dbNameFor(ownerId: string): string {
  return `${DB_PREFIX}:${ownerId}`;
}

let dbPromise: Promise<IDBPDatabase<ProjectionStore>> | null = null;

/**
 * The schema of one owner's store.
 *
 * Extracted so every way a handle is opened — the acting owner's (`db`) and one
 * named owner's (`withOwnerDb`) — migrates identically. A second copy would let
 * the two drift, which is the one thing a schema must never do.
 */
function upgradeDatabase(database: IDBPDatabase<ProjectionStore>): void {
  const opLog = database.createObjectStore('opLog', { keyPath: 'op_id' });
  opLog.createIndex('byNotebook', 'notebook_id');
  opLog.createIndex('byOrdinal', 'localOrdinal');
  opLog.createIndex('bySeq', 'seq');

  database.createObjectStore('notebooks', { keyPath: 'id' });

  const pages = database.createObjectStore('pages', { keyPath: 'id' });
  pages.createIndex('byNotebook', 'notebook_id');

  const tasks = database.createObjectStore('tasks', { keyPath: 'task_id' });
  tasks.createIndex('byNotebook', 'notebook_id');
  tasks.createIndex('byPage', 'page_id');

  database.createObjectStore('session', { keyPath: 'notebook_id' });
  database.createObjectStore('meta', { keyPath: 'key' });
}

/** Opens (and migrates) the current owner's database. Idempotent. */
export function db(): Promise<IDBPDatabase<ProjectionStore>> {
  if (!dbPromise) {
    dbPromise = openOwnerDatabase(dbOwner);
  }
  return dbPromise;
}

/**
 * Opens and migrates one owner's store, and adopts the pre-scoping store into it
 * the first time this browser opens the anonymous owner's.
 */
async function openOwnerDatabase(ownerId: string): Promise<IDBPDatabase<ProjectionStore>> {
  const database = await openDB<ProjectionStore>(dbNameFor(ownerId), DB_VERSION, {
    upgrade: upgradeDatabase,
  });
  await adoptLegacyStore(ownerId, database);
  return database;
}

/**
 * Runs `fn` against a NAMED owner's store — the acting owner's, or one that is
 * merely being addressed — and never moves the acting owner.
 *
 * `setDbOwner` is the wrong tool for a write that belongs to a store this
 * browser is not acting as: it bumps the DB generation, and a store opened while
 * a pin like that is in force is stale for the rest of the page's life (no
 * drain, no session writes). A transient handle for the named owner leaves
 * `dbOwner` and the generation exactly where they were, which is what lets a
 * background write land under a live store without breaking it.
 */
async function withOwnerDb<T>(
  ownerId: string,
  fn: (database: IDBPDatabase<ProjectionStore>) => Promise<T>,
): Promise<T> {
  if (ownerId === dbOwner) return fn(await db());
  const database = await openOwnerDatabase(ownerId);
  try {
    return await fn(database);
  } finally {
    database.close();
  }
}

/** Drops the cached handle. Tests call this between cases. */
export async function resetDbHandle(): Promise<void> {
  if (dbPromise) {
    const handle = await dbPromise;
    handle.close();
  }
  dbPromise = null;
  // The adoption's memo is about the stores that handle was opened against, and
  // they are what just went away — a fresh open asks the question again.
  legacyChecked.clear();
}

// ── the pre-scoping store, adopted once ───────────────────────────────────────

/**
 * The owner whose store has already been checked against the legacy one.
 *
 * Per page, and only a shortcut: the durable half of "this has been adopted
 * already" is the marker in the legacy store itself ([`adoptLegacyStore`]), so
 * a fresh page re-derives the same answer from the data rather than from this.
 */
const legacyChecked = new Set<string>();

/**
 * Carries the pre-scoping `autofocus` store into the anonymous owner's store.
 *
 * The rename from one shared database to `autofocus:<owner>` left an existing
 * browser with a fresh, empty store and no way to see the old one. The relay
 * covers the committed log — the next catch-up replays it — but the outbox is
 * only ever local: an op authored offline and never transmitted exists nowhere
 * else, so without this it would silently stop existing (I6).
 *
 * Three conditions, all of them about not destroying or duplicating anything:
 *
 * * the acting owner must be the ANONYMOUS one — the pre-account owner is the
 *   only owner the legacy store can have belonged to, since accounts did not
 *   exist when it was in use;
 * * the new store must be EMPTY. A store with rows in it is a browser that has
 *   been using the scoped layout already, and importing the old rows under it
 *   would be inventing work;
 * * the legacy store must not carry [`LEGACY_ADOPTED_KEY`]. That marker is what
 *   makes the adoption once-per-browser rather than once-per-empty-store:
 *   sign-out collects the anonymous store (`deleteOtherOwnerDatabases`), so
 *   "empty" alone would re-adopt the same rows on the next boot and hand the
 *   next person on this browser the previous occupant's queue.
 *
 * The copy is ONE IndexedDB transaction, so it is all-or-nothing: a failure
 * leaves the new store exactly as empty as it was and the legacy store untouched,
 * with the work intact in it and the next open free to try again. Only after the
 * copy commits is the legacy store marked, and the mark failing is harmless —
 * the new store then holds the rows, and the emptiness condition already refuses
 * a second adoption.
 *
 * Never throws. A browser whose legacy store cannot be read must still open the
 * store this app actually uses; the failure is logged rather than turned into a
 * boot the user cannot get past.
 */
async function adoptLegacyStore(
  ownerId: string,
  database: IDBPDatabase<ProjectionStore>,
): Promise<void> {
  if (ownerId !== ANONYMOUS_OWNER_ID) return;
  if (legacyChecked.has(ownerId)) return;
  if (!(await isEmptyStore(database))) {
    legacyChecked.add(ownerId);
    return;
  }

  let legacy: IDBDatabase | null = null;
  try {
    legacy = await openLegacyStore();
    if (!legacy) {
      legacyChecked.add(ownerId);
      return;
    }
    if (await legacyWasAdopted(legacy)) {
      legacyChecked.add(ownerId);
      return;
    }
    await copyLegacyStores(legacy, database);
    legacyChecked.add(ownerId);
    await markLegacyAdopted(legacy);
  } catch (error) {
    // Deliberately not fatal, and deliberately not silent. The copy's own
    // transaction did not commit, so nothing is half-adopted and nothing was
    // destroyed: the legacy store still holds every row.
    console.warn('autofocus: could not adopt the pre-scoping local store', error);
  } finally {
    legacy?.close();
  }
}

/** Whether this store has been opened-and-used — no log rows, no meta rows. */
async function isEmptyStore(database: IDBPDatabase<ProjectionStore>): Promise<boolean> {
  return (await database.count('opLog')) === 0 && (await database.count('meta')) === 0;
}

/**
 * The legacy store, or `null` when this browser has none.
 *
 * Opened WITHOUT a version, which is also the existence test: the upgrade only
 * fires for a database that is not there yet, and one this call is about to
 * create is deleted again on the way out so it leaves no trace. `indexedDB.databases()`
 * would be the other way to ask, but it is not universal and this is.
 */
async function openLegacyStore(): Promise<IDBDatabase | null> {
  const request = indexedDB.open(LEGACY_DB_NAME);
  return new Promise((resolve) => {
    let created = false;
    request.onupgradeneeded = () => {
      created = true;
    };
    request.onerror = () => resolve(null);
    request.onsuccess = () => {
      const legacy = request.result;
      if (!created) {
        resolve(legacy);
        return;
      }
      legacy.close();
      const drop = indexedDB.deleteDatabase(LEGACY_DB_NAME);
      const done = () => resolve(null);
      drop.onsuccess = done;
      drop.onerror = done;
      drop.onblocked = done;
    };
  });
}

/** Whether the legacy store says it has already been carried across. */
async function legacyWasAdopted(legacy: IDBDatabase): Promise<boolean> {
  const transaction = legacy.transaction('meta', 'readonly');
  const marker = await requestAsPromise(transaction.objectStore('meta').get(LEGACY_ADOPTED_KEY));
  return marker !== undefined;
}

/**
 * Copies all six stores across, in ONE transaction over the new database.
 *
 * The rows are read out of the legacy store first and written second: the two
 * live in different databases, and an IndexedDB transaction cannot span them.
 * Nothing is transformed — the schemas are the same one, indexes included, so a
 * row put back verbatim keeps its index entries.
 */
async function copyLegacyStores(
  legacy: IDBDatabase,
  database: IDBPDatabase<ProjectionStore>,
): Promise<void> {
  const rows = new Map<(typeof STORE_NAMES)[number], unknown[]>();
  for (const name of STORE_NAMES) {
    const transaction = legacy.transaction(name, 'readonly');
    rows.set(name, (await requestAsPromise(transaction.objectStore(name).getAll())) as unknown[]);
  }

  const transaction = database.transaction(STORE_NAMES, 'readwrite');
  for (const name of STORE_NAMES) {
    // The stores hold different row types; the loop is over the union of them,
    // and every row is copied verbatim, so the cast is the loop's own shape.
    const store = transaction.objectStore(name) as unknown as {
      put(value: unknown): Promise<unknown>;
    };
    for (const row of rows.get(name) ?? []) await store.put(row);
  }
  await transaction.done;
}

/**
 * Marks the legacy store as adopted, so the next boot does not carry the same
 * rows across again (`deleteOtherOwnerDatabases` empties the anonymous store at
 * sign-out, which would otherwise re-open the door).
 *
 * Written into the source rather than the destination on purpose: the
 * destination is the store that comes and goes.
 */
async function markLegacyAdopted(legacy: IDBDatabase): Promise<void> {
  const transaction = legacy.transaction('meta', 'readwrite');
  await requestAsPromise(
    transaction.objectStore('meta').put({ key: LEGACY_ADOPTED_KEY, value: true }),
  );
}

/** One IndexedDB request as a promise. */
function requestAsPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Switches which owner this browser is acting as, and so which store its writes
 * land in (design §5).
 *
 * The two halves move together on purpose. Signing in changes both the account
 * the relay will accept a write from and the local cache that write belongs to;
 * letting one move without the other is how a signed-in user ends up addressing
 * the anonymous owner (refused as cross-owner) or reading the previous person's
 * notebooks. The store is opened here rather than lazily so a caller cannot
 * forget which owner `db()` will answer for.
 *
 * The handler is dropped and the generation bumped: an open store keeps no
 * handle of its own, so without this a store left over from the previous owner
 * would silently read and write the new owner's database.
 */
export async function setDbOwner(ownerId: string): Promise<void> {
  if (ownerId !== dbOwner) {
    await resetDbHandle();
    dbOwner = ownerId;
    dbGeneration += 1;
  }
  await db();
}

/** Deletes the current owner's database. Tests only. */
export async function deleteDatabase(): Promise<void> {
  await resetDbHandle();
  dbGeneration += 1;
  await deleteNamedDatabase(dbNameFor(dbOwner));
}

/**
 * Deletes EVERY local database, whichever owner it belongs to. Tests only.
 *
 * Owner-scoped names are not a closed set — only `indexedDB.databases()` can see
 * a store nobody currently has open — so a test that switches owners cannot
 * clean up after itself with `deleteDatabase` alone.
 */
export async function deleteAllDatabases(): Promise<void> {
  await resetDbHandle();
  dbGeneration += 1;
  for (const name of await localDatabaseNames()) await deleteNamedDatabase(name);
}

/**
 * Collects every OTHER owner's store on this browser, keeping `keepOwnerId`'s
 * (design §5: "sign-out clears the *other* owner's leftover stores").
 *
 * Per-owner scoping is what protects the next person from the person who just
 * signed out: their store has a different name, so it is simply not the store
 * the next account reads, and the leaver's own store survives intact for their
 * next sign-in. What scoping does NOT do is clean up after a PREVIOUS occupant.
 * The one store every browser keeps and shares is the anonymous one (the
 * pre-account owner), and it can hold the earlier person's notebooks: the
 * committed log and the cached projection rows stay where they were authored
 * when an identity change moves only the pending outbox across the boundary
 * (`NotebookStore.setIdentity`). An offline boot resolves as the anonymous owner
 * (`account.ts`), so those leftovers are exactly what the next person on this
 * browser could read. Sign-out is the moment they are collected — the browser is
 * known to be between identities, and the account that is leaving is the only
 * owner whose store is worth keeping.
 *
 * Scoped to this app's own names (`DB_PREFIX:`): the origin may hold databases
 * this module did not name, and a production path must never delete them.
 */
export async function deleteOtherOwnerDatabases(keepOwnerId: string): Promise<void> {
  const keep = dbNameFor(keepOwnerId);
  // Drop the open handle first: a database with a live connection is blocked
  // from deletion until that connection closes, and `dbOwner` is one of the
  // stores being collected whenever `keepOwnerId` is an account.
  await resetDbHandle();
  dbGeneration += 1;
  for (const name of await localDatabaseNames()) {
    if (name === keep) continue;
    if (!name.startsWith(`${DB_PREFIX}:`)) continue;
    await deleteNamedDatabase(name);
  }
}

/**
 * Every local database name in this origin, best-effort and deduped.
 *
 * `indexedDB.databases()` is the only way to see a store nobody has open, but it
 * is not universal; without it the one name this module can build — the acting
 * owner's — is all there is.
 */
async function localDatabaseNames(): Promise<string[]> {
  const factory = indexedDB as IDBFactory & { databases?: IDBFactory['databases'] };
  if (typeof factory.databases !== 'function') return [dbNameFor(dbOwner)];
  const names = (await factory.databases()).flatMap((info) => (info.name ? [info.name] : []));
  return [...new Set(names)];
}

/**
 * One `indexedDB.deleteDatabase`.
 *
 * `onblocked` RESOLVES rather than rejecting: another context holding the
 * database open is a state the suite already handles by dropping the handle
 * first, and a rejection here would turn a delete into a hard test error.
 */
async function deleteNamedDatabase(name: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve();
  });
}

/**
 * Bumped whenever the database a store opened against goes away or is replaced:
 * by `deleteDatabase`, by `deleteAllDatabases`, and by an owner switch
 * (`setDbOwner`). A store captures it when it opens; a flush whose generation no
 * longer matches is inert. Without this, a store from a *previous* test keeps its
 * scheduled-flush timer alive into the next test and — now that every browser
 * shares one `notebook_id` — drains the new test's outbox onto the old test's
 * relay, marking its ops rejected. Shared ids made the outbox filter
 * (`notebook_id`) no longer a firewall between stores. An owner switch is the
 * same hazard with a larger blast radius: the next owner's store holds a
 * different person's work.
 */
let dbGeneration = 0;

/** The current DB generation, for stores to compare against. */
export function currentDbGeneration(): number {
  return dbGeneration;
}

// ── meta store ────────────────────────────────────────────────────────────────

/** Reads one meta value. */
export async function getMeta<T>(key: string): Promise<T | undefined> {
  const database = await db();
  const row = await database.get('meta', key);
  return row?.value as T | undefined;
}

/** Writes one meta value. */
export async function setMeta(key: string, value: unknown): Promise<void> {
  const database = await db();
  await database.put('meta', { key, value });
}

/**
 * Reads one meta value out of a NAMED owner's store, whoever is acting.
 *
 * The account cache lives in the pre-login (anonymous) owner's store, because
 * that is the store the boot can read before it knows whose store to open
 * (`account.ts`). Reading it must therefore not depend on — or change — who the
 * browser is acting as.
 */
export async function getMetaForOwner<T>(ownerId: string, key: string): Promise<T | undefined> {
  return withOwnerDb(ownerId, async (database) => {
    const row = await database.get('meta', key);
    return row?.value as T | undefined;
  });
}

/**
 * Writes one meta value into a NAMED owner's store, whoever is acting.
 *
 * The write half of `getMetaForOwner`: the background re-validation lands while
 * the app is live and acting as the account, and pinning the anonymous store with
 * `setDbOwner` to reach it would stale every store opened in the window.
 */
export async function setMetaForOwner(
  ownerId: string,
  key: string,
  value: unknown,
): Promise<void> {
  await withOwnerDb(ownerId, async (database) => {
    await database.put('meta', { key, value });
  });
}

// ── opLog / outbox ────────────────────────────────────────────────────────────

/** The next local ordinal. Monotonic per device and never reused. */
export async function nextOrdinal(): Promise<number> {
  const database = await db();
  const tx = database.transaction('opLog', 'readwrite');
  const index = tx.store.index('byOrdinal');
  const cursor = await index.openKeyCursor(null, 'prev');
  const next = cursor ? Number(cursor.key) + 1 : 0;
  await tx.done;
  return next;
}

/** Appends an authored envelope to the durable outbox (step 1 of §6.1). */
export async function appendToOutbox(
  envelope: SyncEnvelope,
  deviceId: string,
): Promise<OpRow> {
  const database = await db();
  const tx = database.transaction('opLog', 'readwrite');
  const index = tx.store.index('byOrdinal');
  const cursor = await index.openKeyCursor(null, 'prev');
  const localOrdinal = cursor ? Number(cursor.key) + 1 : 0;

  const row: OpRow = {
    op_id: envelope.op_id,
    notebook_id: envelope.notebook_id,
    device_id: deviceId,
    localOrdinal,
    seq: null,
    envelope,
    committed: null,
  };
  await tx.store.put(row);
  await tx.done;
  return row;
}

/**
 * The next batch to transmit, in local-ordinal order (A11).
 *
 * The outbox drains strictly in ordinal order, one batch at a time; op N+1 is
 * never transmitted until op N's result is known. It is NEVER reordered by
 * timestamp — client clocks are not trusted (§6.1).
 */
export async function peekOutbox(
  notebookId: string,
  limit: number,
): Promise<OpRow[]> {
  const database = await db();
  const rows: OpRow[] = [];
  let cursor = await database
    .transaction('opLog')
    .store.index('byOrdinal')
    .openCursor();

  while (cursor && rows.length < limit) {
    if (cursor.value.notebook_id === notebookId && cursor.value.seq === null) {
      rows.push(cursor.value);
    }
    cursor = await cursor.continue();
  }
  return rows;
}

/** Every committed envelope, ascending by seq — the input to `fold`. */
export async function committedOps(
  notebookId: string,
): Promise<CommittedEnvelope[]> {
  const database = await db();
  const rows: OpRow[] = [];
  let cursor = await database
    .transaction('opLog')
    .store.index('bySeq')
    .openCursor();

  // `bySeq` cannot index nulls meaningfully; skip them and sort by seq.
  while (cursor) {
    if (cursor.value.notebook_id === notebookId && cursor.value.committed) {
      rows.push(cursor.value);
    }
    cursor = await cursor.continue();
  }

  rows.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  return rows.map((r) => r.committed!);
}

/** The unacknowledged envelopes, in local-ordinal order — the outbox. */
export async function pendingOps(notebookId: string): Promise<SyncEnvelope[]> {
  const database = await db();
  const rows: OpRow[] = [];
  let cursor = await database
    .transaction('opLog')
    .store.index('byOrdinal')
    .openCursor();

  while (cursor) {
    if (cursor.value.notebook_id === notebookId && cursor.value.seq === null) {
      rows.push(cursor.value);
    }
    cursor = await cursor.continue();
  }
  rows.sort((a, b) => a.localOrdinal - b.localOrdinal);
  return rows.map((r) => r.envelope);
}

/**
 * Every notebook this device has authored but never refreshed — the outbox's
 * contribution to the notebook list's offline fallback (STEP_7_SPEC.md D7).
 *
 * Deduped by `notebook_id`: the create is the only op a fresh notebook's
 * outbox can hold that names the notebook itself.
 */
export async function pendingCreateNotebooks(): Promise<
  Array<{ notebook_id: string; name: string; tag: string; description?: string; page_capacity: number }>
> {
  const database = await db();
  const rows: OpRow[] = [];
  let cursor = await database
    .transaction('opLog')
    .store.index('byOrdinal')
    .openCursor();
  while (cursor) {
    if (cursor.value.seq === null && cursor.value.envelope.op.type === 'CreateNotebook') {
      rows.push(cursor.value);
    }
    cursor = await cursor.continue();
  }
  const seen = new Set<string>();
  const out: Array<{ notebook_id: string; name: string; tag: string; description?: string; page_capacity: number }> = [];
  for (const row of rows) {
    if (seen.has(row.notebook_id)) continue;
    seen.add(row.notebook_id);
    const op = row.envelope.op;
    if (op.type === 'CreateNotebook') {
      out.push({
        notebook_id: row.notebook_id,
        name: op.name,
        tag: op.tag ?? '',
        description: op.description ?? '',
        page_capacity: op.page_capacity,
      });
    }
  }
  return out;
}

/** Stamps a committed envelope onto its row, clearing it from the outbox. */
export async function markCommitted(
  opId: string,
  seq: number,
  committed: CommittedEnvelope,
): Promise<void> {
  const database = await db();
  const tx = database.transaction('opLog', 'readwrite');
  const row = await tx.store.get(opId);
  if (row) {
    row.seq = seq;
    row.committed = committed;
    await tx.store.put(row);
  }
  await tx.done;
}

/** Records a rejected or malformed verdict so the outbox stops offering it. */
export async function markRejected(opId: string): Promise<void> {
  const database = await db();
  const tx = database.transaction('opLog', 'readwrite');
  const row = await tx.store.get(opId);
  if (row) {
    // A frozen verdict (I5, A17) means the op can never later succeed, so it
    // leaves the outbox. It is NOT deleted: the log is append-only, and the row
    // is the record that it was authored and refused.
    row.seq = -1;
    row.committed = null;
    await tx.store.put(row);
  }
  await tx.done;
}

/**
 * Discards the pending outbox rows for one notebook, leaving committed rows.
 *
 * The 410 path: the relay purged the notebook, so pending rows can never
 * commit. They are deleted outright (not frozen) — the notebook itself is
 * going away with `dropLocalNotebook`.
 */
export async function discardOutbox(notebookId: string): Promise<void> {
  const database = await db();
  const tx = database.transaction('opLog', 'readwrite');
  let cursor = await tx.store.openCursor();
  while (cursor) {
    if (cursor.value.notebook_id === notebookId && cursor.value.seq === null) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }
  await tx.done;
}

/**
 * Drops every local row for one notebook: opLog (committed + pending),
 * the cached projection rows, the session cell, and the `meta.notebookList`
 * entry.
 *
 * The deleting device calls this on 2xx; any device calls it after learning
 * the notebook is gone (§2). Never re-authors — the id stays dropped.
 */
export async function dropLocalNotebook(notebookId: string): Promise<void> {
  const database = await db();
  const opTx = database.transaction('opLog', 'readwrite');
  let opCursor = await opTx.store.openCursor();
  while (opCursor) {
    if (opCursor.value.notebook_id === notebookId) {
      await opCursor.delete();
    }
    opCursor = await opCursor.continue();
  }
  await opTx.done;

  const cacheTx = database.transaction(
    ['notebooks', 'pages', 'tasks', 'session'],
    'readwrite',
  );
  await cacheTx.objectStore('notebooks').delete(notebookId);
  for (const store of ['pages', 'tasks'] as const) {
    const index = cacheTx.objectStore(store).index('byNotebook');
    let cursor = await index.openCursor(IDBKeyRange.only(notebookId));
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
  }
  await cacheTx.objectStore('session').delete(notebookId);
  await cacheTx.done;

  const list = await getMeta<NotebookSummaryLike[]>('notebookList');
  if (list) {
    await setMeta(
      'notebookList',
      list.filter((n) => n.notebook_id !== notebookId),
    );
  }
}

/** The `meta.notebookList` row shape, minus the relay (avoids a cycle). */
interface NotebookSummaryLike {
  notebook_id: string;
}

/** Stores committed envelopes pushed downstream or fetched by catch-up. */
export async function storeCommitted(
  envelopes: CommittedEnvelope[],
  deviceId: string,
): Promise<void> {
  if (envelopes.length === 0) return;
  const database = await db();
  const tx = database.transaction('opLog', 'readwrite');
  const index = tx.store.index('byOrdinal');
  const cursor = await index.openKeyCursor(null, 'prev');
  let next = cursor ? Number(cursor.key) + 1 : 0;

  for (const envelope of envelopes) {
    const existing = await tx.store.get(envelope.op_id);
    if (existing) {
      // Idempotent by seq: a re-delivery updates in place rather than
      // duplicating (§8.3).
      existing.seq = envelope.seq;
      existing.committed = envelope;
      await tx.store.put(existing);
    } else {
      const row: OpRow = {
        op_id: envelope.op_id,
        notebook_id: envelope.notebook_id,
        device_id: envelope.device_id,
        localOrdinal: next++,
        seq: envelope.seq,
        envelope: toSyncEnvelope(envelope),
        committed: envelope,
      };
      await tx.store.put(row);
    }
  }
  await tx.done;
}

/**
 * Projects a `CommittedEnvelope` back to its authored `SyncEnvelope`.
 *
 * Only `client_time` and `logical_day_hint` are not on the committed record, and
 * neither is read by the fold (I2) — `client_time` is display-only and the hint
 * was already resolved into `assigned.logical_day`. Reconstructing them here
 * means one row shape serves both halves of `RenderModel`.
 */
export function toSyncEnvelope(envelope: CommittedEnvelope): SyncEnvelope {
  return {
    op_id: envelope.op_id,
    owner_id: envelope.owner_id,
    device_id: envelope.device_id,
    notebook_id: envelope.notebook_id,
    base_seq: envelope.seq,
    client_time: envelope.server_time,
    logical_day_hint: null,
    op: envelope.op,
  };
}

// ── session store (Channel 2, mirrored locally) ───────────────────────────────

export async function readSession(
  notebookId: string,
): Promise<AutofocusSession | undefined> {
  const database = await db();
  return database.get('session', notebookId);
}

export async function writeSession(cell: AutofocusSession): Promise<void> {
  const database = await db();
  await database.put('session', cell);
}

/**
 * Merges an incoming session cell by lexicographic max on `(lamport, device_id)`.
 *
 * Deterministic, commutative, and idempotent: arrival order cannot change the
 * outcome, so no round trip between devices is needed (REPLICATION.md §5.2).
 */
export function mergeSession(
  a: AutofocusSession,
  b: AutofocusSession,
): AutofocusSession {
  const ka: [number, string] = [a.lamport, a.device_id];
  const kb: [number, string] = [b.lamport, b.device_id];
  if (kb[0] > ka[0] || (kb[0] === ka[0] && kb[1] > ka[1])) return b;
  return a;
}